import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['rowContainer', 'rowTemplate', 'row']
  static values = {
    singleUseFields: { type: Array, default: [] },
    filterKeys: { type: Array, default: [] }
  }

  connect() {
    if (this.rowTargets.length === 0) {
      this.addRow()
    }
    this.updateFieldAvailability()
    this.updateAndIndicators()
    this.injectHiddenInputsIntoSearchForm()
  }

  addRow(event) {
    if (event) event.preventDefault()
    const template = this.rowTemplateTarget.content.cloneNode(true)
    const suffix = Math.random().toString(36).slice(2, 8)

    // Replace all IDs and autocomplete unique-id values to avoid collisions
    template.querySelectorAll('[id]').forEach(el => {
      el.id = el.id.replace(/[^_]+$/, suffix)
    })
    template.querySelectorAll('[data-custom-autocomplete-unique-id-value]').forEach(el => {
      const current = el.getAttribute('data-custom-autocomplete-unique-id-value')
      el.setAttribute('data-custom-autocomplete-unique-id-value', current.replace(/[^_]+$/, suffix))
    })

    this.rowContainerTarget.appendChild(template)
    this.updateFieldAvailability()
    this.updateAndIndicators()
  }

  removeRow(event) {
    event.preventDefault()
    const row = event.target.closest(`[data-${this.identifier}-target="row"]`)
    if (row) {
      row.remove()
      if (this.rowTargets.length === 0) {
        this.addRow()
      }
      this.updateFieldAvailability()
      this.updateAndIndicators()
    }
  }

  fieldChanged(event) {
    const row = event.target.closest(`[data-${this.identifier}-target="row"]`)
    const field = event.target.value
    const valueContainers = row.querySelectorAll('[data-filter-value]')

    // Capture typeahead value from the previously visible filter before switching
    const previousTypeaheadValue = this.#extractTypeaheadValue(row)

    valueContainers.forEach(container => {
      if (container.dataset.filterValue === field) {
        container.classList.remove('d-none')
        container.querySelectorAll('input, select').forEach(input => (input.disabled = false))
      } else {
        container.classList.add('d-none')
        container.querySelectorAll('input, select').forEach(input => (input.disabled = true))
      }
    })

    // If switching to a typeahead filter, carry over the previous typeahead selection
    const newContainer = row.querySelector(`[data-filter-value="${field}"]`)
    if (previousTypeaheadValue && newContainer?.dataset.filterType === 'typeahead') {
      this.#applyTypeaheadValue(newContainer, previousTypeaheadValue)
    }

    this.updateFieldAvailability()
  }

  updateAndIndicators() {
    const lastIndex = this.rowTargets.length - 1
    this.rowTargets.forEach((row, index) => {
      const indicator = row.querySelector('[data-and-indicator]')
      if (indicator) indicator.classList.toggle('d-none', index === lastIndex)
    })
  }

  updateFieldAvailability() {
    const usedFields = new Set()
    this.rowTargets.forEach(row => {
      const select = row.querySelector('[data-filter-field]')
      if (select?.value && this.singleUseFieldsValue.includes(select.value)) {
        usedFields.add(select.value)
      }
    })

    this.rowTargets.forEach(row => {
      const select = row.querySelector('[data-filter-field]')
      if (!select) return
      const currentValue = select.value

      this.singleUseFieldsValue.forEach(field => {
        const option = select.querySelector(`option[value="${field}"]`)
        if (option) {
          option.disabled = usedFields.has(field) && currentValue !== field
        }
      })
    })
  }

  applyFilters(event) {
    event.preventDefault()
    const url = new URL(window.location.href)

    this.filterKeysValue.forEach(key => url.searchParams.delete(key))
    url.searchParams.delete('page')

    this.rowTargets.forEach(row => {
      const fieldSelect = row.querySelector('[data-filter-field]')
      if (!fieldSelect || !fieldSelect.value) return

      const field = fieldSelect.value
      const valueContainer = row.querySelector(`[data-filter-value="${field}"]`)
      if (!valueContainer) return

      const filterType = valueContainer.dataset.filterType
      const paramName = valueContainer.dataset.filterParam

      if (filterType === 'composite_select') {
        const primary = valueContainer.querySelector('[data-filter-primary]')
        const secondary = valueContainer.querySelector('[data-filter-secondary]')
        if (primary?.value && secondary?.value) {
          const secondaryVal = secondary.value === 'none' ? '' : secondary.value
          url.searchParams.append(paramName, `${primary.value}:${secondaryVal}`)
        }
      } else if (filterType === 'select') {
        const select = valueContainer.querySelector('select:not([data-filter-field])')
        if (select?.value) {
          url.searchParams.set(paramName, select.value)
        }
      } else if (filterType === 'typeahead') {
        const hiddenInput = valueContainer.querySelector('input.hidden-typeahead-input')
        if (hiddenInput?.value) {
          url.searchParams.set(paramName, hiddenInput.value)
        }
      }
    })

    window.Turbo.visit(url.toString())
  }

  injectHiddenInputsIntoSearchForm() {
    const searchForm = document.querySelector('#filter-container')?.closest('form')
    if (!searchForm) return

    const url = new URL(window.location.href)
    this.filterKeysValue.forEach(key => {
      const values = url.searchParams.getAll(key)
      values.forEach(value => {
        const input = document.createElement('input')
        input.type = 'hidden'
        input.name = key
        input.value = value
        input.dataset.advancedFilter = 'true'
        searchForm.appendChild(input)
      })
    })
  }

  #extractTypeaheadValue(row) {
    const visibleTypeahead = row.querySelector('[data-filter-type="typeahead"]:not(.d-none)')
    if (!visibleTypeahead) return null

    const hidden = visibleTypeahead.querySelector('input.hidden-typeahead-input')
    return hidden?.value || null
  }

  #applyTypeaheadValue(container, value) {
    const hidden = container.querySelector('input.hidden-typeahead-input')
    if (!hidden || hidden.value === value) return

    hidden.value = value

    // Find the custom-autocomplete controller and trigger display
    const autocompleteEl = container.querySelector('[data-controller*="custom-autocomplete"]')
    if (autocompleteEl) {
      const controller = this.application.getControllerForElementAndIdentifier(autocompleteEl, 'custom-autocomplete')
      if (controller) controller.displayItemSelection()
    }
  }
}
